/**
 * Project Name: Brainsie eCommerce v4
 * Component: Learn More CTA
 * Description: Call-to-action button component positioned at bottom right
 * Author: Brainsie Team
 * Date Created: October 29, 2025
 */

import React from "react";
import { Button, Box } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export default function LearnMoreCTA({
  component,
  section,
  darkMode,
  theme,
  settings = {},
}) {
  // Get component data using correct field mapping
  const primaryText = component.Primary_Text || "";
  const secondaryText = component.Secondary_Text || "";
  const descriptiveText = component.Descriptive_Text || "";
  const primaryCTA = component.Primary_CTA || "";
  const secondaryCTA = component.Secondary_CTA || "";

  // Determine button text and URL from multiple sources
  const buttonText = primaryCTA || primaryText || "Learn More";
  const buttonUrl = secondaryText || "#";
  const secondaryButtonText = secondaryCTA || "";

  // Enhanced component settings with comprehensive defaults
  const componentSettings = {
    size: "medium",
    variant: "contained",
    color: "primary",
    fullWidth: false,
    disabled: false,
    fontSize: "",
    fontWeight: "",
    textAlign: "right", // Default align right for CTA
    backgroundColor: "",
    textColor: "",
    borderRadius: "",
    padding: "",
    margin: "",
    elevation: 0,
    customStyles: "",
    showIcon: true,
    ...settings,
    ...(component.Component_Settings
      ? typeof component.Component_Settings === "string"
        ? JSON.parse(component.Component_Settings)
        : component.Component_Settings
      : {}),
  };

  // Don't render if no button text
  if (
    (!buttonText || buttonText === "NA") &&
    (!secondaryButtonText || secondaryButtonText === "NA")
  ) {
    return null;
  }

  // Get comprehensive styling
  const getComponentStyles = () => ({
    mb: componentSettings.margin ? 0 : 2,
    padding: componentSettings.padding || 0,
    margin: componentSettings.margin || "16px 0",
    backgroundColor: componentSettings.backgroundColor || "transparent",
    borderRadius: componentSettings.borderRadius || "0px",
    display: "flex",
    justifyContent:
      componentSettings.textAlign === "left"
        ? "flex-start"
        : componentSettings.textAlign === "center"
        ? "center"
        : "flex-end",
    alignItems: "center",
    gap: 2,
    flexWrap: "wrap",
    width: componentSettings.fullWidth ? "100%" : "auto",
    opacity: componentSettings.disabled ? 0.6 : 1,
    ...(componentSettings.customStyles
      ? (() => {
          try {
            return typeof componentSettings.customStyles === "string"
              ? JSON.parse(componentSettings.customStyles)
              : componentSettings.customStyles;
          } catch {
            return {};
          }
        })()
      : {}),
  });

  const getButtonStyles = (isPrimary = true) => {
    const baseSize = componentSettings.size;
    return {
      borderRadius: componentSettings.borderRadius || "25px",
      textTransform: "none",
      fontWeight: componentSettings.fontWeight || (isPrimary ? 600 : 500),
      fontSize:
        componentSettings.fontSize ||
        (baseSize === "large"
          ? "1.1rem"
          : baseSize === "small"
          ? "0.875rem"
          : "1rem"),
      px:
        componentSettings.padding ||
        (baseSize === "large" ? 4 : baseSize === "small" ? 2 : 3),
      py:
        componentSettings.padding ||
        (baseSize === "large" ? 1.5 : baseSize === "small" ? 1 : 1.25),
      boxShadow:
        componentSettings.elevation > 0
          ? `0px ${componentSettings.elevation}px ${
              componentSettings.elevation * 2
            }px rgba(0,0,0,0.12)`
          : "0 2px 8px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: componentSettings.disabled ? "none" : "translateY(-2px)",
        boxShadow: componentSettings.disabled
          ? "none"
          : "0 4px 16px rgba(0,0,0,0.15)",
      },
      "&:disabled": {
        opacity: 0.6,
        cursor: "not-allowed",
      },
    };
  };

  const handlePrimaryClick = () => {
    if (
      buttonUrl &&
      buttonUrl !== "#" &&
      buttonUrl !== "NA" &&
      !componentSettings.disabled
    ) {
      if (buttonUrl.startsWith("http")) {
        window.open(buttonUrl, "_blank");
      } else {
        window.location.href = buttonUrl;
      }
    }
  };

  const handleSecondaryClick = () => {
    // Secondary button could have its own action or just be informational
    console.log("Secondary CTA clicked:", secondaryButtonText);
  };

  return (
    <Box sx={getComponentStyles()}>
      {/* Descriptive Text */}
      {descriptiveText && (
        <Box sx={{ mb: 2 }}>
          <p
            style={{
              color:
                componentSettings.textColor ||
                (darkMode
                  ? theme.palette.grey[300]
                  : theme.palette.text.primary),
              margin: "0",
              fontSize: componentSettings.fontSize || "1rem",
              fontWeight: componentSettings.fontWeight || "normal",
              textAlign: componentSettings.textAlign || "right",
              lineHeight: 1.5,
            }}
          >
            {descriptiveText}
          </p>
        </Box>
      )}

      {/* Secondary CTA Button (if exists) */}
      {secondaryButtonText && secondaryButtonText !== "NA" && (
        <Button
          variant="outlined"
          color={componentSettings.color || "primary"}
          onClick={handleSecondaryClick}
          disabled={componentSettings.disabled}
          sx={getButtonStyles(false)}
        >
          {secondaryButtonText}
        </Button>
      )}

      {/* Primary CTA Button */}
      {buttonText && buttonText !== "NA" && (
        <Button
          variant={componentSettings.variant || "contained"}
          color={componentSettings.color || "primary"}
          onClick={handlePrimaryClick}
          disabled={componentSettings.disabled}
          endIcon={
            componentSettings.showIcon !== false ? <ArrowForwardIcon /> : null
          }
          sx={getButtonStyles(true)}
        >
          {buttonText}
        </Button>
      )}
    </Box>
  );
}
